import React, { useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * TimeSlotPicker - Select preferred tee time slots
 * Supports specific 30-min slots, ranges (early/middle/late), and before/after cutoffs
 * Mobile-first design
 */

const TIME_SLOT_OPTIONS = {
  specific: [
    { value: '7:00', label: '7:00 AM', description: 'First tee time' },
    { value: '7:30', label: '7:30 AM', description: '' },
    { value: '8:00', label: '8:00 AM', description: '' },
    { value: '8:30', label: '8:30 AM', description: '' },
    { value: '9:00', label: '9:00 AM', description: '' },
    { value: '9:30', label: '9:30 AM', description: '' },
    { value: '10:00', label: '10:00 AM', description: 'Later morning' },
  ],
  ranges: [
    { value: 'early', label: 'Early', description: '7:00 - 8:00 AM' },
    { value: 'middle', label: 'Middle', description: '8:00 - 9:00 AM' },
    { value: 'late', label: 'Late', description: '9:00 - 10:00 AM' },
  ],
  before: [
    { value: 'before-8:00', label: 'Before 8:00', description: 'First 2 tee times' },
    { value: 'before-8:30', label: 'Before 8:30', description: 'First 3 tee times' },
    { value: 'before-9:00', label: 'Before 9:00', description: 'First 4 tee times' },
    { value: 'before-9:30', label: 'Before 9:30', description: 'First 5 tee times' },
  ],
  after: [
    { value: 'after-8:00', label: 'After 8:00', description: '8:30 AM and later' },
    { value: 'after-8:30', label: 'After 8:30', description: '9:00 AM and later' },
    { value: 'after-9:00', label: 'After 9:00', description: '9:30 AM and later' },
    { value: 'after-9:30', label: 'After 9:30', description: '10:00 AM' },
  ],
};

const TimeSlotPicker = ({
  value = 'no-preference',
  onChange,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState(() => {
    // Determine initial tab based on current value
    if (value === 'no-preference') return 'ranges';
    if (['7:00', '7:30', '8:00', '8:30', '9:00', '9:30', '10:00'].includes(value)) return 'specific';
    if (['early', 'middle', 'late'].includes(value)) return 'ranges';
    if (value.startsWith('before-')) return 'before';
    if (value.startsWith('after-')) return 'after';
    return 'ranges';
  });

  const tabs = [
    { id: 'ranges', label: 'Ranges' },
    { id: 'specific', label: 'Specific' },
    { id: 'before', label: 'Before' },
    { id: 'after', label: 'After' },
  ];

  const handleSelect = (slotValue) => {
    onChange(slotValue);
  };

  const isSelected = (slotValue) => value === slotValue;

  const renderSlotButton = (slot) => (
    <button
      key={slot.value}
      type="button"
      onClick={() => handleSelect(slot.value)}
      className={cn(
        'w-full p-4 rounded-lg border-2 text-left transition-all',
        isSelected(slot.value)
          ? 'border-green-500 bg-green-50 ring-2 ring-green-500 ring-opacity-50'
          : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
      )}
    >
      <div className="font-medium text-gray-900">{slot.label}</div>
      {slot.description && (
        <div className="text-sm text-gray-500 mt-1">{slot.description}</div>
      )}
    </button>
  );

  return (
    <div className={cn('space-y-4', className)}>
      {/* No Preference Option */}
      <button
        type="button"
        onClick={() => handleSelect('no-preference')}
        className={cn(
          'w-full p-4 rounded-lg border-2 text-left transition-all',
          value === 'no-preference'
            ? 'border-green-500 bg-green-50 ring-2 ring-green-500 ring-opacity-50'
            : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
        )}
      >
        <div className="font-medium text-gray-900">No Preference</div>
        <div className="text-sm text-gray-500 mt-1">Any available tee time works for me</div>
      </button>

      {/* Tab Navigation */}
      <div className="flex rounded-lg bg-gray-100 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors',
              activeTab === tab.id
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-2">
        {activeTab === 'specific' && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Select a specific 30-minute tee time slot:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {TIME_SLOT_OPTIONS.specific.map(renderSlotButton)}
            </div>
          </>
        )}

        {activeTab === 'ranges' && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Choose a general time range:
            </p>
            <div className="space-y-2">
              {TIME_SLOT_OPTIONS.ranges.map(renderSlotButton)}
            </div>
          </>
        )}

        {activeTab === 'before' && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              I want to play before a certain time:
            </p>
            <div className="space-y-2">
              {TIME_SLOT_OPTIONS.before.map(renderSlotButton)}
            </div>
          </>
        )}

        {activeTab === 'after' && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              I want to play after a certain time:
            </p>
            <div className="space-y-2">
              {TIME_SLOT_OPTIONS.after.map(renderSlotButton)}
            </div>
          </>
        )}
      </div>

      {/* Current Selection Display */}
      {value !== 'no-preference' && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="text-sm text-green-800">
            <span className="font-medium">Current preference: </span>
            {getSlotDescription(value)}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get human-readable description
function getSlotDescription(value) {
  if (value === 'no-preference') return 'No preference';

  const allOptions = [
    ...TIME_SLOT_OPTIONS.specific,
    ...TIME_SLOT_OPTIONS.ranges,
    ...TIME_SLOT_OPTIONS.before,
    ...TIME_SLOT_OPTIONS.after,
  ];

  const option = allOptions.find(o => o.value === value);
  if (option) {
    return option.description
      ? `${option.label} (${option.description})`
      : option.label;
  }

  return value;
}

export { TimeSlotPicker, TIME_SLOT_OPTIONS, getSlotDescription };
export default TimeSlotPicker;
